import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import AdminLayout from '../../components/AdminLayout';
import {
  FaFileUpload, FaDownload, FaInfoCircle, FaFileExcel,
  FaFileCsv, FaCheckCircle, FaTimesCircle, FaCloudUploadAlt,
  FaCloudDownloadAlt, FaHistory, FaChevronDown, FaSpinner,
  FaDatabase, FaListAlt, FaTrash
} from 'react-icons/fa';

function Import() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [feedRules, setFeedRules] = useState(null);
  const [activeTab, setActiveTab] = useState('import');
  const [dragOver, setDragOver] = useState(false);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportStatus, setExportStatus] = useState('');
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get('/import/logs').then(res => setLogs(res.data)).catch(console.error);
    api.get('/import/feed-rules').then(res => setFeedRules(res.data)).catch(console.error);
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/import/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      api.get('/import/logs').then(res => setLogs(res.data));
    } catch (err) {
      setResult({ error: err.response?.data?.error || 'Import failed.' });
    }
    setLoading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && /\.(csv|xlsx|xls)$/i.test(f.name)) {
      setFile(f);
    }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem('token');
    const baseURL = api.defaults.baseURL;
    window.open(`${baseURL}/import/template?token=${token}`, '_blank');
  };

  const handleExport = async (type) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const baseURL = api.defaults.baseURL;
      const statusParam = exportStatus ? `&status=${exportStatus}` : '';

      if (type === 'resources') {
        window.open(`${baseURL}/import/export?format=${exportFormat}${statusParam}&token=${token}`, '_blank');
      } else {
        window.open(`${baseURL}/import/export-categories?token=${token}`, '_blank');
      }
    } catch (err) {
      console.error(err);
    }
    setTimeout(() => setExporting(false), 1000);
  };

  const getFileIcon = () => {
    if (!file) return <FaCloudUploadAlt />;
    if (file.name.endsWith('.csv')) return <FaFileCsv />;
    return <FaFileExcel />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <AdminLayout>
      <div className="ie-page">
        {/* Header */}
        <div className="ie-header">
          <div>
            <h2 className="ie-title">Data Manager</h2>
            <p className="ie-subtitle">Import & export resources and categories</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="ie-tabs">
          <button
            className={`ie-tab ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            <FaCloudUploadAlt /> Import
          </button>
          <button
            className={`ie-tab ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
          >
            <FaCloudDownloadAlt /> Export
          </button>
          <button
            className={`ie-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <FaHistory /> History
          </button>
        </div>

        {/* IMPORT TAB */}
        {activeTab === 'import' && (
          <div className="ie-content">
            {/* Feed Rules Card */}
            {feedRules && (
              <div className="ie-rules-card">
                <div className="ie-rules-header">
                  <FaInfoCircle />
                  <span>Feed Rules</span>
                  <span className="ie-rules-badge">Like Google Merchant Center</span>
                </div>
                <div className="ie-rules-body">
                  <div className="ie-rules-cols">
                    <div className="ie-rules-col">
                      <h4>Required Fields</h4>
                      <div className="ie-field-tags">
                        {feedRules.rules.required.map(f => (
                          <span key={f} className="ie-field-tag required">{f}</span>
                        ))}
                      </div>
                    </div>
                    <div className="ie-rules-col">
                      <h4>Optional Fields</h4>
                      <div className="ie-field-tags">
                        {feedRules.rules.optional.map(f => (
                          <span key={f} className="ie-field-tag optional">{f}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="ie-example">
                    <h4>Example Row</h4>
                    <div className="ie-example-grid">
                      {Object.entries(feedRules.example).map(([k, v]) => (
                        <div key={k} className="ie-example-field">
                          <span className="ie-example-key">{k}</span>
                          <span className="ie-example-val">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Area */}
            <form onSubmit={handleUpload}>
              <div
                className={`ie-dropzone ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileRef}
                  accept=".csv,.xlsx,.xls"
                  onChange={e => setFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <div className="ie-dropzone-icon">{getFileIcon()}</div>
                {file ? (
                  <div className="ie-file-info">
                    <span className="ie-file-name">{file.name}</span>
                    <span className="ie-file-size">{formatFileSize(file.size)}</span>
                    <button type="button" className="ie-file-remove" onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (fileRef.current) fileRef.current.value = '';
                    }}>
                      <FaTrash /> Remove
                    </button>
                  </div>
                ) : (
                  <div className="ie-dropzone-text">
                    <p className="ie-dropzone-main">Drop your file here or click to browse</p>
                    <p className="ie-dropzone-hint">Supports CSV, XLSX, XLS files</p>
                  </div>
                )}
              </div>

              <div className="ie-actions">
                <button type="submit" className="ie-btn ie-btn-primary" disabled={!file || loading}>
                  {loading ? <><FaSpinner className="spin" /> Importing...</> : <><FaFileUpload /> Upload & Import</>}
                </button>
                <button type="button" className="ie-btn ie-btn-secondary" onClick={downloadTemplate}>
                  <FaDownload /> Download Template
                </button>
              </div>
            </form>

            {/* Result */}
            {result && (
              <div className={`ie-result ${result.error ? 'error' : 'success'}`}>
                <div className="ie-result-icon">
                  {result.error ? <FaTimesCircle /> : <FaCheckCircle />}
                </div>
                <div className="ie-result-body">
                  <p className="ie-result-msg">{result.error || result.message}</p>
                  {!result.error && (
                    <div className="ie-result-stats">
                      <span className="ie-stat success"><FaCheckCircle /> {result.imported} imported</span>
                      {result.failed > 0 && <span className="ie-stat error"><FaTimesCircle /> {result.failed} failed</span>}
                      <span className="ie-stat total"><FaDatabase /> {result.total} total</span>
                    </div>
                  )}
                  {result.errors && result.errors.length > 0 && (
                    <details className="ie-errors-detail">
                      <summary>{result.errors.length} error(s) — click to expand</summary>
                      <ul>{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </details>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* EXPORT TAB */}
        {activeTab === 'export' && (
          <div className="ie-content">
            <div className="ie-export-grid">
              {/* Resources Export */}
              <div className="ie-export-card">
                <div className="ie-export-card-icon resources">
                  <FaDatabase />
                </div>
                <h3>Export Resources</h3>
                <p>Download all resources with name, link, description, category, tags, status, and views.</p>

                <div className="ie-export-options">
                  <div className="ie-export-option">
                    <label>Format</label>
                    <div className="ie-format-toggle">
                      <button
                        className={`ie-format-btn ${exportFormat === 'xlsx' ? 'active' : ''}`}
                        onClick={() => setExportFormat('xlsx')}
                      >
                        <FaFileExcel /> XLSX
                      </button>
                      <button
                        className={`ie-format-btn ${exportFormat === 'csv' ? 'active' : ''}`}
                        onClick={() => setExportFormat('csv')}
                      >
                        <FaFileCsv /> CSV
                      </button>
                    </div>
                  </div>

                  <div className="ie-export-option">
                    <label>Filter by Status</label>
                    <div className="ie-select-wrap">
                      <select value={exportStatus} onChange={e => setExportStatus(e.target.value)}>
                        <option value="">All Resources</option>
                        <option value="published">Published Only</option>
                        <option value="pending">Pending Only</option>
                        <option value="rejected">Rejected Only</option>
                      </select>
                      <FaChevronDown className="ie-select-arrow" />
                    </div>
                  </div>
                </div>

                <button
                  className="ie-btn ie-btn-primary ie-btn-full"
                  onClick={() => handleExport('resources')}
                  disabled={exporting}
                >
                  {exporting ? <><FaSpinner className="spin" /> Exporting...</> : <><FaCloudDownloadAlt /> Export Resources</>}
                </button>
              </div>

              {/* Categories Export */}
              <div className="ie-export-card">
                <div className="ie-export-card-icon categories">
                  <FaListAlt />
                </div>
                <h3>Export Categories</h3>
                <p>Download all categories with name, slug, icon, translation, sort order, and resource count.</p>

                <div className="ie-export-options" style={{ minHeight: 120 }}>
                  <div className="ie-export-info">
                    <FaInfoCircle />
                    <span>Exports as XLSX with all category data including resource counts per category.</span>
                  </div>
                </div>

                <button
                  className="ie-btn ie-btn-primary ie-btn-full"
                  onClick={() => handleExport('categories')}
                  disabled={exporting}
                >
                  {exporting ? <><FaSpinner className="spin" /> Exporting...</> : <><FaCloudDownloadAlt /> Export Categories</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div className="ie-content">
            {logs.length === 0 ? (
              <div className="ie-empty">
                <FaHistory />
                <p>No import history yet</p>
              </div>
            ) : (
              <div className="ie-history-list">
                {logs.map(log => (
                  <div key={log.id} className="ie-history-item">
                    <div className="ie-history-icon">
                      <FaFileExcel />
                    </div>
                    <div className="ie-history-info">
                      <span className="ie-history-name">{log.filename}</span>
                      <span className="ie-history-meta">
                        by {log.imported_by_name} · {new Date(log.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="ie-history-stats">
                      <span className="ie-history-stat success">{log.imported} imported</span>
                      {log.failed > 0 && <span className="ie-history-stat error">{log.failed} failed</span>}
                      <span className="ie-history-stat total">{log.total_rows} total</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default Import;
